import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Tabs,
  Tab,
  Snackbar
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import SubscriptionTable from './SubscriptionTable';
import SubscriptionDetailsPanel from './SubscriptionDetailsPanel';
import SubscriptionExpirationWarnings from './SubscriptionExpirationWarnings';
import CreateSubscriptionDialog from './CreateSubscriptionDialog';
import ManageTeamsDialog from './ManageTeamsDialog';
import EditReferenceNameDialog from './EditReferenceNameDialog';
import ManageAddOnsDialog from './ManageAddOnsDialog';
import PaymentMethodsManager from './PaymentMethodsManager';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';
import useSubscriptions from '../../hooks/useSubscriptions';
import useOrganizationTeams from '../../hooks/useOrganizationTeams';
import { useOrganization } from '../../context/OrganizationContext';
import { usePermissionVisibility } from '../../hooks/usePermissions';
import { Subscription } from '../../types';

interface SubscriptionManagementProps {
  organizationId: string;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ organizationId }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // State
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [createSubDialogOpen, setCreateSubDialogOpen] = useState(false);
  const [manageAddOnsOpen, setManageAddOnsOpen] = useState(false);
  const [manageTeamsOpen, setManageTeamsOpen] = useState(false);
  const [editReferenceNameOpen, setEditReferenceNameOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  // Hooks
  const {
    subscriptions,
    isLoading,
    error,
    cancelSubscription,
    reactivateSubscription,
    deleteSubscription,
    createSubscription,
    updateSubscriptionAddOns,
    assignSubscriptionToTeam,
    updateSubscriptionReferenceName,
    refreshSubscriptions
  } = useSubscriptions(organizationId);
  
  const { teams, refreshTeams } = useOrganizationTeams(organizationId);
  const { refreshData } = useOrganization();

  // Permission checks
  const {
    showPaymentMethodsTab,
    showCreateSubscriptionButton,
    showManageAddOnsButton,
    showEditSubscriptionButton,
    showCancelSubscriptionButton,
    showDeleteSubscriptionButton,
    showAssignTeamsButton
  } = usePermissionVisibility();

  // Debug logging
  React.useEffect(() => {
    console.log('SubscriptionManagement - organizationId:', organizationId);
    console.log('SubscriptionManagement - subscriptions:', subscriptions);
    console.log('SubscriptionManagement - isLoading:', isLoading);
    console.log('SubscriptionManagement - error:', error);
  }, [organizationId, subscriptions, isLoading, error]);

  // Keep selectedSubscription in sync when subscriptions array updates
  React.useEffect(() => {
    if (selectedSubscription) {
      const updatedSub = subscriptions.find(s =>
        s.id === selectedSubscription.id || s._id === selectedSubscription._id ||
        s.id === selectedSubscription._id || s._id === selectedSubscription.id
      );
      if (updatedSub && JSON.stringify(updatedSub) !== JSON.stringify(selectedSubscription)) {
        setSelectedSubscription(updatedSub);
      }
    }
  }, [subscriptions, selectedSubscription]);

  // Show errors in snackbar instead of persistent error display
  React.useEffect(() => {
    if (error) {
      setAlertMessage(error.message || 'Failed to load subscriptions');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  }, [error]);

  // Handlers
  const handleRowClick = (subscription: Subscription) => {
    // Toggle selection - if clicking the same row, deselect it
    if (selectedSubscription && (selectedSubscription.id === subscription.id || selectedSubscription._id === subscription._id)) {
      setSelectedSubscription(null);
    } else {
      setSelectedSubscription(subscription);
    }
  };

  const handleCancelSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setConfirmCancelOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedSubscription) return;

    await cancelSubscription(selectedSubscription.id || selectedSubscription._id);
    setConfirmCancelOpen(false);
  };

  const handleReactivateSubscription = async (subscription: Subscription) => {
    const result = await reactivateSubscription(subscription.id || subscription._id);
    if (result.success) {
      console.log('Subscription reactivated successfully');
      // The subscriptions list and selectedSubscription will be updated automatically
      // by the hook and the useEffect above
    }
  };

  const handleDeleteSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubscription) return;
    setConfirmDeleteOpen(false);
    const result = await deleteSubscription(selectedSubscription.id || selectedSubscription._id);
    if (result.success) {
      console.log('Subscription deleted successfully');
      setAlertMessage('Subscription deleted successfully');
      setAlertSeverity('success');
      setAlertOpen(true);
    } else {
      let errorMsg = result.error?.message || 'Failed to delete subscription';
      // Strip gRPC error code prefix (e.g., "9 FAILED_PRECONDITION: ")
      errorMsg = errorMsg.replace(/^\d+\s+[A-Z_]+:\s+/, '');
      console.error('Failed to delete subscription:', errorMsg);
      setAlertMessage(errorMsg);
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };
  
  const handleAssignTeams = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setManageTeamsOpen(true);
  };

  const handleAssignTeam = async (teamId: string) => {
    if (!selectedSubscription) return { success: false, error: { message: 'No subscription selected' } };

    try {
      const result = await assignSubscriptionToTeam(teamId, selectedSubscription.id || selectedSubscription._id);

      if (result.success) {
        // Refresh all data to update team and subscription statistics
        console.log('Refreshing data after team subscription assignment...');
        try {
          await Promise.all([
            refreshData(),      // Refresh organization context
            refreshTeams(),     // Refresh teams list with updated subscriptionId values
            refreshSubscriptions(true)  // Refresh subscriptions list
          ]);
          console.log('All data refreshed successfully');
        } catch (refreshError) {
          console.warn('Failed to refresh data:', refreshError);
          // Don't fail assignment if refresh fails - user can manually refresh
        }
      }

      return result;
    } catch (error: any) {
      return { success: false, error: { message: error.message || 'Failed to assign subscription to team' } };
    }
  };

  const handleManageAddOns = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setManageAddOnsOpen(true);
  };

  const handleUpdateAddOns = async (
    addOnSelections: Array<{
      priceId: string;
      quantity: number;
      name: string;
      pricePerPack: number;
    }>,
    paymentMethodId?: string
  ) => {
    if (!selectedSubscription) return { success: false, error: { message: 'No subscription selected' } };

    try {
      // Transform selections to API format (priceId + quantity)
      const addOns = addOnSelections.map(selection => ({
        priceId: selection.priceId,
        quantity: selection.quantity
      }));

      // Call the update API with add-on array
      const result = await updateSubscriptionAddOns(
        selectedSubscription.id || selectedSubscription._id,
        { addOns, paymentMethodId } as any
      );

      // Refresh subscriptions list after successful add-on purchase (force refresh to bypass cache)
      if (result.success) {
        await refreshSubscriptions(true);
      }

      return result;
    } catch (error: any) {
      return { success: false, error: { message: error.message || 'Failed to update add-ons' } };
    }
  };
  
  const handleCreateSubscription = () => {
    setCreateSubDialogOpen(true);
  };

  const handleCreateSubscriptionSuccess = async () => {
    setCreateSubDialogOpen(false);

    // Refresh subscriptions list (force refresh to bypass cache)
    await refreshSubscriptions(true);

    // IMPORTANT: Also refresh the global OrganizationContext so CreateTeamDialog sees the new subscription
    console.log('[SubscriptionManagement] Refreshing global OrganizationContext after subscription creation');
    await refreshData();
  };

  const handleEditReferenceName = (subscription?: Subscription) => {
    const targetSubscription = subscription || selectedSubscription;
    if (!targetSubscription) return;
    setSelectedSubscription(targetSubscription);
    setEditReferenceNameOpen(true);
  };
  
  const handleUpdateReferenceName = async (referenceName: string) => {
    if (!selectedSubscription) return { success: false, error: { message: 'No subscription selected' } };
    
    try {
      const result = await updateSubscriptionReferenceName(
        selectedSubscription.id || selectedSubscription._id,
        referenceName
      );
      return result;
    } catch (error: any) {
      return { success: false, error: { message: error.message || 'Failed to update reference name' } };
    }
  };
  
  // Placeholder empty state
  const EmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h6" gutterBottom>
        No Subscriptions Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {showCreateSubscriptionButton
          ? 'Create a subscription to manage billing and resource limits for your organization.'
          : 'Your organization does not have any subscriptions yet. Contact your organization administrator to create subscriptions.'}
      </Typography>
      {showCreateSubscriptionButton && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleCreateSubscription}
        >
          Create Subscription
        </Button>
      )}
    </Box>
  );
  
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Subscription Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your organization's subscriptions, billing, and resource limits.
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Subscriptions" />
          {showPaymentMethodsTab && <Tab label="Payment Methods" />}
        </Tabs>

        {/* Subscriptions Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Subscriptions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and manage your organization's active subscriptions, billing periods, and resource limits.
            </Typography>

            {/* Expiration Warnings */}
            <SubscriptionExpirationWarnings
              subscriptions={subscriptions}
              onManageSubscription={handleRowClick}
            />

            {/* Subscriptions Table */}
            {subscriptions.length === 0 && !isLoading ? (
              <EmptyState />
            ) : (
              <SubscriptionTable
                subscriptions={subscriptions}
                isLoading={isLoading}
                selectedSubscriptionId={selectedSubscription?.id || selectedSubscription?._id}
                onRowClick={handleRowClick}
                onCancel={handleCancelSubscription}
                onReactivate={handleReactivateSubscription}
                onDelete={handleDeleteSubscription}
                onAssignTeams={handleAssignTeams}
                onManageAddOns={handleManageAddOns}
                onEditReferenceName={handleEditReferenceName}
                onCreateSubscription={handleCreateSubscription}
                showEditButton={showEditSubscriptionButton}
                showManageAddOnsButton={showManageAddOnsButton}
                showCancelButton={showCancelSubscriptionButton}
                showDeleteButton={showDeleteSubscriptionButton}
                showAssignTeamsButton={showAssignTeamsButton}
                showCreateButton={showCreateSubscriptionButton}
              />
            )}

            {/* Subscription Details Panel - shown below table when subscription is selected */}
            {selectedSubscription && (
              <Box sx={{ mt: 3 }}>
                <SubscriptionDetailsPanel
                  subscription={selectedSubscription}
                  teams={teams}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Payment Methods Tab */}
        {activeTab === 1 && showPaymentMethodsTab && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Methods
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add and manage payment methods for your organization's subscriptions.
            </Typography>
            <PaymentMethodsManager organizationId={organizationId} />
          </Box>
        )}
      </Paper>
      
      {/* Dialogs - These would be implemented as separate components */}
      <ConfirmDialog
        open={confirmCancelOpen}
        title="Cancel Subscription"
        content={`Are you sure you want to cancel the ${selectedSubscription?.name} subscription? You will still have access until the end of your current billing period.`}
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
        onConfirm={handleCancelConfirm}
        onCancel={() => setConfirmCancelOpen(false)}
        severity="warning"
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete Subscription"
        content="Are you sure you want to permanently delete this expired subscription? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        severity="error"
      />
      
      {/* Create Subscription Dialog */}
      <CreateSubscriptionDialog
        open={createSubDialogOpen}
        onClose={() => setCreateSubDialogOpen(false)}
        onSuccess={handleCreateSubscriptionSuccess}
        organizationId={organizationId}
      />
      
      {/* Manage Teams Dialog */}
      <ManageTeamsDialog
        open={manageTeamsOpen}
        onClose={() => setManageTeamsOpen(false)}
        subscription={selectedSubscription}
        subscriptions={subscriptions}
        teams={teams}
        onAssign={handleAssignTeam}
      />
      
      {/* Edit Reference Name Dialog */}
      <EditReferenceNameDialog
        open={editReferenceNameOpen}
        onClose={() => setEditReferenceNameOpen(false)}
        onSubmit={handleUpdateReferenceName}
        subscription={selectedSubscription}
      />

      {/* Manage Add-ons Dialog */}
      <ManageAddOnsDialog
        open={manageAddOnsOpen}
        onClose={() => setManageAddOnsOpen(false)}
        onSubmit={handleUpdateAddOns}
        subscription={selectedSubscription}
        organizationId={organizationId}
      />

      {/* Note: Additional dialogs to be implemented */}
      {/*
        - EditBillingDialog
        - BillingHistoryDialog
      */}

      {/* Alert Snackbar for operation feedback */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{
            width: '100%',
            backgroundColor: alertSeverity === 'success' ? 'success.main' :
                           alertSeverity === 'error' ? 'error.main' :
                           alertSeverity === 'warning' ? 'warning.main' :
                           'info.main',
            color: 'white',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManagement;